import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  extractSectionWithMaxScoreFromArticles,
  shareholdersWithShares,
  total,
} from "@/dbot/common/commonDomain";
import { ShareholderDTO } from "@/types/Shareholder";
import { identity, sortBy } from "lodash";
import { newUuid } from "@/utils/uuid";
import { getAllocationStatus } from "@/shareholderRegister/transactions/issue/issueTransactionDomain";
import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "@/common/commonForms";
import { ShareType } from "@/common/share";

export type CapitalIncreaseThroughBonusIssue = {
  type: DecisionType.capitalIncreaseThroughBonusIssue;
  resolutionIsBasedOnIntermediateBalanceSheet: boolean;
  intermediateBalanceSheetDate?: string;
  properties: BonusIssueType;
  articleOfAssociationNumber?: number;
};

export enum CapitalIncreaseThroughBonusIssueType {
  shareBonusIssue = "shareBonusIssue",
  increaseNominalValueByBonusIssue = "increaseNominalValueByBonusIssue",
}

const capitalIncreaseThroughBonusIssueTypeCaption: {
  [key in CapitalIncreaseThroughBonusIssueType]: TMessage;
} = {
  [CapitalIncreaseThroughBonusIssueType.shareBonusIssue]: {
    key: "decisions.capitalIncreaseThroughBonusIssue.sharesOrNominalValue.newShares",
  },
  [CapitalIncreaseThroughBonusIssueType.increaseNominalValueByBonusIssue]: {
    key: "decisions.capitalIncreaseThroughBonusIssue.sharesOrNominalValue.increaseNominalValue",
  },
};

export type BonusIssueType = ShareBonusIssue | IncreaseNominalValueByBonusIssue;

export type IncreaseNominalValueByBonusIssue = {
  type: CapitalIncreaseThroughBonusIssueType.increaseNominalValueByBonusIssue;
  increaseInNominalValue?: number;
};

export type ShareBonusIssue = {
  type: CapitalIncreaseThroughBonusIssueType.shareBonusIssue;
  shareTypeIssues: ShareTypeBonusIssue[];
  subscribers: SubscriberForBonusIssue[];
};

export type ShareTypeBonusIssue = {
  sharetypeId: number;
  numberOfShares?: number;
};

export type SubscriberForBonusIssue = {
  sharetypeId: number;
  shareholderId: number;
  numberOfShares: number;
};

export class CapitalIncreaseThroughBonusIssueForm {
  readonly type = DecisionType.capitalIncreaseThroughBonusIssue;

  dispatch: DbotEventDispatcher;

  resolutionIsBasedOnIntermediateBalanceSheetModel: BooleanFieldModel;
  intermediateBalanceSheetDateModel: LocalDateFieldModel;

  increaseNominalValueByBonusIssue: IncreaseNominalValueByBonusIssueModel;
  shareBonusIssue: ShareBonusIssueModel;
  properties: BonusIssueTypeModel;
  articleOfAssociationNumber: ArticleSelectorModel;

  bonusIssueTypeModel: RadioFieldModel<CapitalIncreaseThroughBonusIssueType | undefined>;

  constructor(
    dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<CapitalIncreaseThroughBonusIssue>,
    public data?: CapitalIncreaseThroughBonusIssue,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;

    this.resolutionIsBasedOnIntermediateBalanceSheetModel = new BooleanFieldModel({
      value: data?.resolutionIsBasedOnIntermediateBalanceSheet ?? false,
    });

    this.intermediateBalanceSheetDateModel = new LocalDateFieldModel({
      value: data?.intermediateBalanceSheetDate,
      required: data?.resolutionIsBasedOnIntermediateBalanceSheet,
    });
    this.increaseNominalValueByBonusIssue = new IncreaseNominalValueByBonusIssueModel(
      asIncreaseNominalValueByBonusIssue(data?.properties),
    );
    this.shareBonusIssue = new ShareBonusIssueModel(
      this.shareholders,
      this.shareTypes,
      asShareBonusIssue(data?.properties),
    );

    this.properties =
      data?.properties.type === "shareBonusIssue"
        ? this.shareBonusIssue
        : this.increaseNominalValueByBonusIssue;
    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );

    this.bonusIssueTypeModel = new RadioFieldModel<
      CapitalIncreaseThroughBonusIssueType | undefined
    >({
      value: data?.properties.type,
      options: Object.values(CapitalIncreaseThroughBonusIssueType),
      idFn: identity,
      labelFn: (value) =>
        value ? capitalIncreaseThroughBonusIssueTypeCaption[value] : { translated: "" },
      onChange: (value) => this.changeBonusIssueType(value as CapitalIncreaseThroughBonusIssueType),
    });
  }

  init() {
    if (!this.articleOfAssociationNumber.selected) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.articleOfAssociationNumber.selected) {
          this.articleOfAssociationNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
    }
  }

  setIntermediateBalanceSheetDateRequired() {
    this.intermediateBalanceSheetDateModel.setRequired(
      this.resolutionIsBasedOnIntermediateBalanceSheetModel.value,
    );
  }

  changeBonusIssueType(value?: CapitalIncreaseThroughBonusIssueType) {
    if (value === "shareBonusIssue") {
      this.properties = this.shareBonusIssue;
    } else {
      this.properties = this.increaseNominalValueByBonusIssue;
    }
  }

  validate(): boolean {
    this.resolutionIsBasedOnIntermediateBalanceSheetModel.validate();
    this.intermediateBalanceSheetDateModel.validate();
    this.properties.validate();
    return this.isValid;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.resolutionIsBasedOnIntermediateBalanceSheetModel.isValid &&
      this.intermediateBalanceSheetDateModel.isValid &&
      this.properties.isValid &&
      this.articleOfAssociationNumber.isValid
    );
  }

  get shareholders() {
    return shareholdersWithShares(
      this.context.initialState.shareholders,
      this.context.inputState?.sharesForShareholders,
    );
  }

  get shareTypes() {
    return this.context.initialState.shareTypes;
  }

  get currentNominalValue() {
    return this.context.inputState?.sharenominalValue ?? 0;
  }

  get currentNumberOfShares() {
    return total(this.context.inputState?.numberOfShares);
  }

  get showShareIssue() {
    return this.properties.type === "shareBonusIssue";
  }

  get showIncreaseNominalValue() {
    return this.properties.type === "increaseNominalValueByBonusIssue";
  }
}

export type BonusIssueTypeModel = ShareBonusIssueModel | IncreaseNominalValueByBonusIssueModel;

export class IncreaseNominalValueByBonusIssueModel {
  type: CapitalIncreaseThroughBonusIssueType.increaseNominalValueByBonusIssue;
  increaseInNominalValue: NumberFieldModel;

  constructor(data?: IncreaseNominalValueByBonusIssue) {
    this.type = CapitalIncreaseThroughBonusIssueType.increaseNominalValueByBonusIssue;
    this.increaseInNominalValue = new NumberFieldModel({
      value: data?.increaseInNominalValue,
      isPositiveOrZero: true,
      required: true,
      isInteger: false,
    });
    makeAutoObservable(this);
  }

  get isValid(): boolean {
    return this.increaseInNominalValue.isValid;
  }

  validate(): boolean {
    return this.increaseInNominalValue.validate();
  }
}

export class ShareBonusIssueModel {
  type: CapitalIncreaseThroughBonusIssueType.shareBonusIssue;
  shareTypes: ShareType[];
  shareholders: ShareholderDTO[];
  shareTypeIssues: ShareTypeBonusIssueModel[];
  subscribers: SubscriberForBonusIssueModel[];

  constructor(shareholders: ShareholderDTO[], shareTypes: ShareType[], data?: ShareBonusIssue) {
    this.type = CapitalIncreaseThroughBonusIssueType.shareBonusIssue;
    this.shareTypes = shareTypes;
    this.shareholders = shareholders;
    this.shareTypeIssues =
      shareTypes.map(
        (shareType) =>
          new ShareTypeBonusIssueModel(
            shareType,
            data?.shareTypeIssues.find((d) => shareType.sharetypeId === d.sharetypeId),
          ),
      ) ?? [];
    this.subscribers = sortBy(shareholders, "name").flatMap((shareholder) =>
      shareTypes.map(
        (shareType) =>
          new SubscriberForBonusIssueModel(
            shareType,
            shareholder,
            data?.subscribers.find(
              (subscriber) =>
                subscriber.shareholderId === shareholder.id &&
                subscriber.sharetypeId === shareType.sharetypeId,
            ),
          ),
      ),
    );
  }

  get activeShareTypeIds(): number[] {
    return this.shareTypeIssues
      .filter((s) => !s.numberOfShares.isEmpty)
      .map((s) => s.shareType.sharetypeId);
  }

  get activeSubscribers(): SubscriberForBonusIssueModel[] {
    return this.subscribers.filter((s) => !s.numberOfShares.isEmpty);
  }

  get isValid(): boolean {
    return (
      !this.shareTypeIssues.every((d) => d.isEmpty) &&
      this.shareTypeIssues.every((d) => d.isValid) &&
      this.subscribers.every((subscriber) => subscriber.isValid) &&
      getAllocationStatus(this.shareTypeIssues, this.subscribers).every((s) => s.remaining === 0)
    );
  }

  validate(): boolean {
    this.shareTypeIssues.every((d) => d.validate());
    this.subscribers.every((d) => d.validate());
    return this.isValid;
  }
}

export class ShareTypeBonusIssueModel {
  readonly shareType: ShareType;
  numberOfShares: NumberFieldModel;

  constructor(shareType: ShareType, data?: ShareTypeBonusIssue) {
    this.shareType = shareType;
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
    });
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate();
  }
}
export class SubscriberForBonusIssueModel {
  id = newUuid();
  shareholder: ShareholderDTO;
  shareType: ShareType;
  numberOfShares: NumberFieldModel;

  constructor(shareType: ShareType, shareholder: ShareholderDTO, data?: SubscriberForBonusIssue) {
    this.shareholder = shareholder;
    this.shareType = shareType;
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
    });
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate();
  }
}

function asShareBonusIssue(data: BonusIssueType | undefined): ShareBonusIssue | undefined {
  if (data?.type === "shareBonusIssue") {
    return data;
  }
}

function asIncreaseNominalValueByBonusIssue(
  data: BonusIssueType | undefined,
): IncreaseNominalValueByBonusIssue | undefined {
  if (data?.type === "increaseNominalValueByBonusIssue") {
    return data;
  }
}

export function toBonusIssueType(model: BonusIssueTypeModel): BonusIssueType {
  if (model.type === "shareBonusIssue") {
    return toShareBonusIssue(model);
  } else {
    return toIncreaseNominalValueByBonusIssue(model);
  }
}

function toIncreaseNominalValueByBonusIssue(
  model: IncreaseNominalValueByBonusIssueModel,
): IncreaseNominalValueByBonusIssue {
  return {
    type: model.type,
    increaseInNominalValue: model.increaseInNominalValue.parsedValue,
  };
}

function toShareBonusIssue(model: ShareBonusIssueModel): ShareBonusIssue {
  return {
    type: model.type,
    shareTypeIssues: toShareTypeBonusIssue(model.shareTypeIssues),
    subscribers: toSubscriberForBonusIssue(model.activeSubscribers),
  };
}

function toShareTypeBonusIssue(model: ShareTypeBonusIssueModel[]): ShareTypeBonusIssue[] {
  return model.map((shareBonusIssue) => {
    return {
      sharetypeId: shareBonusIssue.shareType.sharetypeId,
      numberOfShares: shareBonusIssue.numberOfShares.parsedValue,
    };
  });
}

function toSubscriberForBonusIssue(
  model: SubscriberForBonusIssueModel[],
): SubscriberForBonusIssue[] {
  return model.map((subscriber) => {
    return {
      sharetypeId: subscriber.shareType.sharetypeId,
      shareholderId: subscriber.shareholder.id,
      numberOfShares: subscriber.numberOfShares.toNumberOrZero(),
    };
  });
}
